/**
 * @fileoverview Mock backend for the Sales CRM My Page write endpoints.
 * @description Registers the My Page commands on the feature's shared mock adapter, answering in the
 * backend's envelopes with its error codes (Confluence 3.5.11.18-.24, decision record
 * docs/decisions/2026-10-31-my-page.md in the backend repo): Update Target Stage, Reopen Target,
 * Reassign Target, Request Target Reassignment, Send Payment Details and Confirm Payment, plus List
 * Eligible Target Reassignees. Each change goes through the shared Target store, so the other
 * sales pages see it, while what that store cannot hold (the `tg1.` revision, when payment details
 * went out, an open reassignment request and the history entries) lives in my-page-mock-state.ts.
 * The commands that change a target quote its version as a strong `If-Match` (428/4104 missing,
 * 412/4121 stale). Simplifications: Rejected is stored as Lost (both close the target), and a
 * reassignment request stays pending, since no reviewer approves it here. Business-rule refusals
 * use 4xx statuses other than 403, which the shared response interceptor would turn into the
 * full-page 403 screen. A no-op when VITE_ENABLE_MOCK_API is false.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { resolveDownlineUserIds } from '@/features/sales/lib/sales-hierarchy.utils'

import {
  bumpRevision,
  cancelPendingReassignment,
  logTargetEvent,
  openPendingReassignment,
  pendingReassignmentOf,
  recordedPaymentSentAt,
  recordPaymentSentAt,
} from './my-page-mock-state'
import {
  indiaToday,
  paymentStateOf,
  personOf,
  targetStatusOf,
  toMyPageRow,
  visibleTo,
} from './my-page-mock.service'
import { hasOrganisationDetails } from './organisation-details-mock.service'
import { mockCaller } from './sales-crm-mock-projection'
import {
  bodyOf,
  failReply,
  headerOf,
  okEnvelope,
  pageReply,
  queryOf,
} from './sales-crm-mock-response.utils'
import { salesMockAdapter } from './sales-mock-adapter'
import { listSalesRoster } from './sales-roster-mock-store'
import {
  confirmTargetPayment,
  findTargetById,
  reassignTarget,
  updateTargetStage,
} from './targets-mock-store'
import { findTenantById } from './tenants-mock-store'

import type { TargetSource } from './my-page-mock.service'
import type { MockReply } from './sales-crm-mock-response.utils'
import type {
  MyPagePaymentDetailsSent,
  MyPagePaymentRecipient,
  MyPageStageCode,
} from '@/features/sales/types/my-page.types'
import type { SalesStage, SalesUserRecord } from '@/features/sales/types/tenant.types'
import type { AxiosRequestConfig } from 'axios'

/** @description The pipeline order of the stages a target can be moved forward to. */
const FORWARD_RANK: Partial<Record<MyPageStageCode, number>> = {
  NEW: 0,
  CONTACTED: 1,
  DEMO_REQUESTED: 2,
  PROPOSAL_SENT: 3,
  UNDER_NEGOTIATION: 4,
  SUBSCRIPTION_REQUESTED: 5,
}

/** @description The stages that need a reason and are not part of the forward order. */
const SIDE_STAGES: readonly MyPageStageCode[] = ['ON_HOLD', 'LOST', 'REJECTED']

/** @description The shared store's stage for each stage a command may move a target to. */
const STORE_STAGE: Partial<Record<MyPageStageCode, SalesStage>> = {
  CONTACTED: 'Contacted',
  DEMO_REQUESTED: 'Meeting/Demo',
  PROPOSAL_SENT: 'Proposal',
  UNDER_NEGOTIATION: 'Negotiation',
  SUBSCRIPTION_REQUESTED: 'Awaiting Payment',
  ON_HOLD: 'On Hold',
  LOST: 'Lost',
  REJECTED: 'Lost',
}

/** @description The longest reason the backend accepts. */
const REASON_MAX_LENGTH = 500

/**
 * @description The target a request names, if the caller may see it.
 * @param {AxiosRequestConfig} config - The request config, for the target id in its URL.
 * @param {SalesUserRecord | undefined} caller - The signed-in caller.
 * @returns {TargetSource | undefined} The target and its bank.
 */
function sourceOf(
  config: AxiosRequestConfig,
  caller: SalesUserRecord | undefined,
): TargetSource | undefined {
  const raw = config.url?.match(/^\/sales\/targets\/([^/]+)\//)?.[1]
  const targetId = raw === undefined ? '' : decodeURIComponent(raw)
  return visibleTo(caller).find((candidate) => candidate.target.id === targetId)
}

/**
 * @description The target as the store holds it right now, with its bank. Unlike {@link sourceOf}
 * it ignores visibility, so a command's answer can describe a target the caller just handed away.
 * @param {string} targetId - The target.
 * @returns {TargetSource} The target and its bank.
 */
function freshSource(targetId: string): TargetSource {
  const target = findTargetById(targetId)
  const tenant = target ? findTenantById(target.tenantId) : undefined
  if (!target || !tenant) throw new Error(`Mock target ${targetId} vanished`)
  return { target, tenant }
}

/**
 * @description Answers a command with the target's row and its new version as the ETag.
 * @param {string} targetId - The target.
 * @param {number} [status] - The HTTP status (200 by default).
 * @returns {MockReply} The reply.
 */
function rowReply(targetId: string, status = 200): MockReply {
  const row = toMyPageRow(freshSource(targetId), indiaToday())
  return [status, okEnvelope(row), { ETag: `"${row.version}"` }]
}

/**
 * @description Checks the `If-Match` a command sent against the target's current version.
 * @param {AxiosRequestConfig} config - The request config.
 * @param {TargetSource} source - The target.
 * @returns {MockReply | null} 428 when missing, 412 when stale, otherwise null.
 */
function versionRefusal(config: AxiosRequestConfig, source: TargetSource): MockReply | null {
  const sent = headerOf(config, 'If-Match')?.replace(/^"|"$/g, '')
  if (sent === undefined) return failReply(428, '4104', 'If-Match is required')
  const current = toMyPageRow(source, indiaToday()).version
  return sent === current ? null : failReply(412, '4121', 'The target changed since it was read')
}

/**
 * @description Refuses a command on a target that is closed or completed (409/4115).
 * @param {TargetSource} source - The target.
 * @returns {MockReply | null} The refusal, or null while the target is open.
 */
function notOpenRefusal(source: TargetSource): MockReply | null {
  return targetStatusOf(source.target) === 'OPEN'
    ? null
    : failReply(409, '4115', 'The target is closed or completed')
}

/**
 * @description Reads the optional `reason` of a command body.
 * @param {Record<string, unknown>} body - The request body.
 * @param {boolean} required - Whether a reason must be given.
 * @returns {{ ok: true; reason?: string } | { ok: false }} The trimmed reason, or a failure when it
 * is missing (and required), not text, or longer than the backend accepts.
 */
function readReason(
  body: Record<string, unknown>,
  required: boolean,
): { ok: true; reason?: string } | { ok: false } {
  if (body.reason === undefined) return required ? { ok: false } : { ok: true }
  if (typeof body.reason !== 'string') return { ok: false }
  const reason = body.reason.trim()
  return reason.length >= 1 && reason.length <= REASON_MAX_LENGTH
    ? { ok: true, reason }
    : { ok: false }
}

/**
 * @description Answers Update Target Stage: forward to a later stage up to Subscription Requested
 * (resuming from On Hold may go to any of them), or to Lost, On Hold or Rejected with a reason.
 * Live and New are never accepted (409/4114).
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {MockReply} The updated row or the refusal.
 */
function updateStageReply(config: AxiosRequestConfig): MockReply {
  const caller = mockCaller()
  const source = sourceOf(config, caller)
  if (!source) return failReply(404, '4113', 'Target not found')
  const refusal = versionRefusal(config, source) ?? notOpenRefusal(source)
  if (refusal) return refusal
  const body = bodyOf(config)
  const toStage = body.toStage as MyPageStageCode | undefined
  const storeStage = toStage ? STORE_STAGE[toStage] : undefined
  if (!toStage) return failReply(400, '0001', 'Validation failed')
  if (!storeStage) return failReply(409, '4114', 'That stage cannot be set by a stage update')
  const needsReason = SIDE_STAGES.includes(toStage)
  const reason = readReason(body, needsReason)
  if (!reason.ok || (!needsReason && body.reason !== undefined)) {
    return failReply(400, '0001', 'A reason is required for Lost, On Hold and Rejected only')
  }
  const row = toMyPageRow(source, indiaToday())
  const fromCode = row.stage.code
  const fromRank = FORWARD_RANK[fromCode]
  const toRank = FORWARD_RANK[toStage]
  const forward =
    toRank !== undefined &&
    toRank >= 1 &&
    (fromCode === 'ON_HOLD' || (fromRank !== undefined && toRank > fromRank))
  const sideMove = SIDE_STAGES.includes(toStage) && toStage !== fromCode
  if (!forward && !sideMove) return failReply(409, '4114', 'That stage move is not allowed')
  const result = updateTargetStage(source.target.id, {
    stage: storeStage,
    ...(reason.reason ? { reason: reason.reason } : {}),
    actingUserId: source.target.ownerId,
  })
  if (!result || 'error' in result) return failReply(422, '1422', result?.error ?? 'Not updated')
  recordPaymentSentAt(source.target.id, null)
  if (toStage === 'LOST' || toStage === 'REJECTED') cancelPendingReassignment(source.target.id)
  logTargetEvent(source.target.id, {
    eventType: 'STAGE_CHANGED',
    fromStage: fromCode,
    toStage,
    fromUser: null,
    toUser: null,
    actor: personOf(caller?.id ?? source.target.ownerId),
    reason: reason.reason ?? null,
    recipientKind: null,
  })
  bumpRevision(source.target.id)
  return rowReply(source.target.id)
}

/**
 * @description Answers Reopen Target: only an Awaiting Payment target (Subscription Requested with
 * the details sent) reopens, back to Under Negotiation, clearing the sent state and any pending
 * reassignment request.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {MockReply} The updated row or the refusal.
 */
function reopenReply(config: AxiosRequestConfig): MockReply {
  const caller = mockCaller()
  const source = sourceOf(config, caller)
  if (!source) return failReply(404, '4113', 'Target not found')
  const refusal = versionRefusal(config, source) ?? notOpenRefusal(source)
  if (refusal) return refusal
  const read = readReason(bodyOf(config), false)
  if (!read.ok) return failReply(400, '0001', 'The reason must be 1 to 500 characters')
  const awaiting =
    source.target.stage === 'Awaiting Payment' && paymentStateOf(source.target) === 'SENT'
  if (!awaiting) return failReply(409, '4114', 'Only an Awaiting Payment target can be reopened')
  updateTargetStage(source.target.id, {
    stage: 'Negotiation',
    actingUserId: source.target.ownerId,
  })
  recordPaymentSentAt(source.target.id, null)
  cancelPendingReassignment(source.target.id)
  logTargetEvent(source.target.id, {
    eventType: 'REOPENED',
    fromStage: 'SUBSCRIPTION_REQUESTED',
    toStage: 'UNDER_NEGOTIATION',
    fromUser: null,
    toUser: null,
    actor: personOf(caller?.id ?? source.target.ownerId),
    reason: read.reason ?? null,
    recipientKind: null,
  })
  bumpRevision(source.target.id)
  return rowReply(source.target.id)
}

/**
 * @description Who the caller may name as a target's new assignee, never the caller and never the
 * current assignee: a Super Admin any active Head, Manager or Executive; a Head the Managers and
 * Executives of their downline; a Manager the Executives of their downline; an Executive the
 * active Executives reporting to the same direct manager.
 * @param {SalesUserRecord} caller - The signed-in caller.
 * @param {TargetSource} source - The target.
 * @returns {SalesUserRecord[]} The eligible people, ordered by name.
 */
function eligibleReassigneesOf(caller: SalesUserRecord, source: TargetSource): SalesUserRecord[] {
  const roster = listSalesRoster()
  const downline = resolveDownlineUserIds(caller.id, roster)
  const eligibleFor = (user: SalesUserRecord): boolean => {
    if (caller.role === 'Super Admin') return user.role !== 'Super Admin'
    if (caller.role === 'Sales Head')
      return downline.includes(user.id) && user.role !== 'Sales Head'
    if (caller.role === 'Sales Manager') {
      return downline.includes(user.id) && user.role === 'Sales Executive'
    }
    return user.role === 'Sales Executive' && user.reportsTo === caller.reportsTo
  }
  return roster
    .filter(
      (user) =>
        user.status === 'Active' &&
        user.id !== caller.id &&
        user.id !== source.target.ownerId &&
        eligibleFor(user),
    )
    .sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * @description Answers List Eligible Target Reassignees: the people the caller may name for an open
 * target (an Awaiting Payment one only for a Super Admin among Heads and Managers).
 * @param {AxiosRequestConfig} config - The request config, for the optional search.
 * @returns {MockReply} The page or the refusal.
 */
function eligibleReassigneesReply(config: AxiosRequestConfig): MockReply {
  const caller = mockCaller()
  const source = sourceOf(config, caller)
  if (!caller || !source) return failReply(404, '4113', 'Target not found')
  const refusal = notOpenRefusal(source)
  if (refusal) return refusal
  const awaiting = paymentStateOf(source.target) === 'SENT'
  if (awaiting && (caller.role === 'Sales Head' || caller.role === 'Sales Manager')) {
    return failReply(409, '4115', 'Only a Super Admin may reassign an Awaiting Payment target')
  }
  const needle = queryOf(config).search?.trim().toLowerCase()
  const people = eligibleReassigneesOf(caller, source)
    .filter((user) => !needle || `${user.name} ${user.id}`.toLowerCase().includes(needle))
    .map((user) => personOf(user.id))
  return pageReply(config, people)
}

/**
 * @description Answers Reassign Target: a Super Admin, Head or Manager moves an open target, with
 * its prospect, to an eligible person. A pending request blocks it (409/4116) and an Awaiting
 * Payment target is a Super Admin's alone.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {MockReply} The updated row or the refusal.
 */
function reassignReply(config: AxiosRequestConfig): MockReply {
  const caller = mockCaller()
  const source = sourceOf(config, caller)
  if (!caller || !source) return failReply(404, '4113', 'Target not found')
  if (caller.role === 'Sales Executive') {
    return failReply(422, '2008', 'An Executive asks for a reassignment instead')
  }
  const refusal = versionRefusal(config, source) ?? notOpenRefusal(source)
  if (refusal) return refusal
  if (pendingReassignmentOf(source.target.id)) {
    return failReply(409, '4116', 'A reassignment request is pending')
  }
  if (paymentStateOf(source.target) === 'SENT' && caller.role !== 'Super Admin') {
    return failReply(409, '4115', 'Only a Super Admin may reassign an Awaiting Payment target')
  }
  const body = bodyOf(config)
  const reason = readReason(body, false)
  const assigneeId = typeof body.assigneeUserId === 'string' ? body.assigneeUserId : ''
  if (!reason.ok || !assigneeId) return failReply(400, '0001', 'Validation failed')
  if (!eligibleReassigneesOf(caller, source).some((user) => user.id === assigneeId)) {
    return failReply(422, '4106', 'That person cannot be assigned this target')
  }
  const result = reassignTarget(source.target.id, {
    toOwnerId: assigneeId,
    actingUserId: caller.id,
    actingUserRole: caller.role,
  })
  if (!result || 'error' in result) return failReply(422, '4106', result?.error ?? 'Not reassigned')
  bumpRevision(source.target.id)
  return rowReply(source.target.id)
}

/**
 * @description Answers Request Target Reassignment: an Executive asks to hand their own open
 * target, not yet Awaiting Payment, to an active Executive under the same manager. The target
 * changes only on approval, which the mock never simulates.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {MockReply} The opened request (201) or the refusal.
 */
function requestReassignmentReply(config: AxiosRequestConfig): MockReply {
  const caller = mockCaller()
  const source = sourceOf(config, caller)
  if (!caller || !source || source.target.ownerId !== caller.id) {
    return failReply(404, '4113', 'Target not found')
  }
  if (caller.role !== 'Sales Executive') {
    return failReply(422, '2008', 'Only an Executive requests a reassignment')
  }
  const refusal = notOpenRefusal(source)
  if (refusal) return refusal
  if (paymentStateOf(source.target) === 'SENT') {
    return failReply(409, '4115', 'An Awaiting Payment target cannot be reassigned by request')
  }
  if (pendingReassignmentOf(source.target.id)) {
    return failReply(409, '4116', 'A reassignment request is already pending')
  }
  const body = bodyOf(config)
  const reason = readReason(body, true)
  const assigneeId = typeof body.assigneeUserId === 'string' ? body.assigneeUserId : ''
  if (!reason.ok || !reason.reason || !assigneeId)
    return failReply(400, '0001', 'Validation failed')
  if (!eligibleReassigneesOf(caller, source).some((user) => user.id === assigneeId)) {
    return failReply(422, '4106', 'That person cannot be proposed for this target')
  }
  const opened = openPendingReassignment(source.target.id, {
    proposedAssigneeId: assigneeId,
    requestedById: caller.id,
    reason: reason.reason,
  })
  return [
    201,
    okEnvelope({
      approvalRequestId: opened.approvalRequestId,
      status: 'PENDING',
      proposedAssignee: personOf(assigneeId),
    }),
  ]
}

/**
 * @description Answers Send Payment Details: from the Subscription Requested stage, "emails" the
 * organisation's bank details to the bank's primary contact. The first send sets the sent time; a
 * resend keeps it. Needs an `Idempotency-Key`.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {MockReply} The payment state after the send, or the refusal.
 */
function sendPaymentDetailsReply(config: AxiosRequestConfig): MockReply {
  const caller = mockCaller()
  const source = sourceOf(config, caller)
  if (!source) return failReply(404, '4113', 'Target not found')
  const refusal = notOpenRefusal(source)
  if (refusal) return refusal
  if (headerOf(config, 'Idempotency-Key') === undefined) {
    return failReply(428, '4104', 'Idempotency-Key is required')
  }
  const recipient = bodyOf(config).recipient as MyPagePaymentRecipient | undefined
  if (recipient !== 'PRIMARY' && recipient !== 'SECONDARY') {
    return failReply(400, '0001', 'Validation failed')
  }
  if (source.target.stage !== 'Awaiting Payment') {
    return failReply(409, '4117', 'Payment details go out from the Subscription Requested stage')
  }
  if (!hasOrganisationDetails()) {
    return failReply(409, '4120', 'The organisation details have not been saved')
  }
  if (recipient === 'SECONDARY' || !source.tenant.authorisedContact.email) {
    return failReply(409, '4117', 'That contact has no email address')
  }
  const sentAt = recordedPaymentSentAt(source.target.id) ?? new Date().toISOString()
  recordPaymentSentAt(source.target.id, sentAt)
  logTargetEvent(source.target.id, {
    eventType: 'PAYMENT_SENT',
    fromStage: null,
    toStage: null,
    fromUser: null,
    toUser: null,
    actor: personOf(caller?.id ?? source.target.ownerId),
    reason: null,
    recipientKind: recipient,
  })
  bumpRevision(source.target.id)
  const row = toMyPageRow(freshSource(source.target.id), indiaToday())
  const sent: MyPagePaymentDetailsSent = {
    targetId: row.targetId,
    recipient,
    paymentState: 'SENT',
    paymentSentAt: sentAt,
    displayStatus: row.displayStatus,
    version: row.version,
  }
  return [200, okEnvelope(sent)]
}

/**
 * @description Answers Confirm Payment: a Super Admin confirms a sent payment, completing the
 * target and making the bank an Active Client. Checks in the backend's order: not found, closed,
 * already completed, details not sent, then the version.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {MockReply} The completed row or the refusal.
 */
function confirmPaymentReply(config: AxiosRequestConfig): MockReply {
  const caller = mockCaller()
  const source = sourceOf(config, caller)
  if (!caller || !source) return failReply(404, '4113', 'Target not found')
  if (caller.role !== 'Super Admin') {
    return failReply(422, '2008', 'Only a Super Admin confirms a payment')
  }
  const status = targetStatusOf(source.target)
  if (status === 'CLOSED') return failReply(409, '4115', 'The target is closed')
  if (status === 'COMPLETED') return failReply(409, '4119', 'The payment is already confirmed')
  if (paymentStateOf(source.target) !== 'SENT') {
    return failReply(409, '4118', 'No payment details were sent for this target')
  }
  const stale = versionRefusal(config, source)
  if (stale) return stale
  confirmTargetPayment(source.target.id, caller.id)
  cancelPendingReassignment(source.target.id)
  bumpRevision(source.target.id)
  return rowReply(source.target.id)
}

if (salesMockAdapter) {
  salesMockAdapter.onPost(/^\/sales\/targets\/[^/]+\/stage$/).reply(updateStageReply)
  salesMockAdapter.onPost(/^\/sales\/targets\/[^/]+\/reopen$/).reply(reopenReply)
  salesMockAdapter
    .onGet(/^\/sales\/targets\/[^/]+\/eligible-reassignees$/)
    .reply(eligibleReassigneesReply)
  salesMockAdapter.onPost(/^\/sales\/targets\/[^/]+\/reassign$/).reply(reassignReply)
  salesMockAdapter
    .onPost(/^\/sales\/targets\/[^/]+\/reassignment-requests$/)
    .reply(requestReassignmentReply)
  salesMockAdapter
    .onPost(/^\/sales\/targets\/[^/]+\/payment-details\/send$/)
    .reply(sendPaymentDetailsReply)
  salesMockAdapter.onPost(/^\/sales\/targets\/[^/]+\/payment\/confirm$/).reply(confirmPaymentReply)
}
